import {
  KeyboardArrowDown as ArrowDownIcon,
  Language as LanguageIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Rocket as RocketIcon,
  Star as StarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Notifications as NotificationIcon,
  AccountCircle as AccountIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Avatar,
  Badge,
  Paper,
  Collapse
} from '@mui/material';
import React, { useState, useEffect } from 'react';

const HeaderPremium = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState(null);

  // Enhanced scroll listener
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event, menuName) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenu(menuName);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentMenu(null);
  };

  const handleMobileMenuToggle = (menuName) => {
    setExpandedMobileMenu(expandedMobileMenu === menuName ? null : menuName);
  };

  // Enhanced menu items with descriptions and icons
  const menuItems = [
    {
      title: 'Platform',
      icon: <RocketIcon />,
      description: 'Enterprise solutions',
      items: [
        { name: 'AI Content Management', desc: 'Next-gen CMS with AI', new: true },
        { name: 'Visual Page Builder', desc: 'Drag & drop interface', popular: true },
        { name: 'Mobile Applications', desc: 'iOS & Android native apps' },
        { name: 'Performance Analytics', desc: 'Real-time insights' },
        { name: 'Security Center', desc: 'Enterprise-grade protection' }
      ]
    },
    {
      title: 'Solutions',
      icon: <StarIcon />,
      description: 'Industry-specific',
      items: [
        { name: 'News & Media', desc: 'Digital publishing platform', popular: true },
        { name: 'E-commerce', desc: 'Online store solutions' },
        { name: 'Corporate', desc: 'Enterprise websites' },
        { name: 'Education', desc: 'Learning management systems' },
        { name: 'Healthcare', desc: 'Medical practice management' }
      ]
    },
    {
      title: 'Resources',
      icon: <SearchIcon />,
      description: 'Learn & explore',
      items: [
        { name: 'Documentation', desc: 'Complete guides & tutorials' },
        { name: 'API Reference', desc: 'Developer resources' },
        { name: 'Case Studies', desc: 'Success stories' },
        { name: 'Webinars', desc: 'Live training sessions', new: true },
        { name: 'Community', desc: 'Connect with developers' }
      ]
    }
  ];

  const simpleMenuItems = ['Pricing', 'About', 'Contact'];

  // Enhanced mobile drawer
  const drawer = (
    <Box sx={{ 
      textAlign: 'left', 
      width: 350, 
      height: '100%', 
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(79, 70, 229, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(5, 150, 105, 0.03) 0%, transparent 50%)
        `,
        pointerEvents: 'none'
      }
    }}>
      {/* Enhanced Mobile Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 4,
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        position: 'relative',
        zIndex: 1
      }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 900, 
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '1.8rem'
            }}
          >
            RONESIS
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
            Enterprise Platform
          </Typography>
        </Box>
        <IconButton 
          onClick={handleDrawerToggle} 
          sx={{ 
            color: '#64748b',
            bgcolor: 'rgba(100, 116, 139, 0.1)',
            '&:hover': {
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Enhanced Mobile Menu Items */}
      <List sx={{ px: 3, py: 2, position: 'relative', zIndex: 1 }}>
        {menuItems.map((item, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <ListItem 
              disablePadding
              onClick={() => handleMobileMenuToggle(item.title)}
              sx={{
                borderRadius: 3,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(79, 70, 229, 0.08)'
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  p: 2
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'rgba(79, 70, 229, 0.1)',
                    color: '#4f46e5'
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={700} color="#1e293b">
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {item.description}
                  </Typography>
                </Box>
                <ArrowDownIcon 
                  sx={{ 
                    color: '#64748b',
                    transform: expandedMobileMenu === item.title ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }} 
                />
              </Box>
            </ListItem>
            
            <Collapse in={expandedMobileMenu === item.title}>
              <Box sx={{ pl: 7, pr: 2, py: 1 }}>
                {item.items.map((subItem, subIndex) => (
                  <Box
                    key={subIndex}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(79, 70, 229, 0.05)',
                        transform: 'translateX(8px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" fontWeight={600} color="#334155">
                        {subItem.name}
                      </Typography>
                      {subItem.new && (
                        <Chip 
                          label="New" 
                          size="small" 
                          sx={{ 
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#10b981',
                            color: 'white',
                            fontWeight: 600
                          }} 
                        />
                      )}
                      {subItem.popular && (
                        <Chip 
                          label="Popular" 
                          size="small" 
                          sx={{ 
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: '#f59e0b',
                            color: 'white',
                            fontWeight: 600
                          }} 
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="#64748b" sx={{ mt: 0.5, display: 'block' }}>
                      {subItem.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        ))}
        
        {simpleMenuItems.map((item, index) => (
          <ListItem 
            key={index}
            disablePadding
            sx={{
              mb: 1,
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(79, 70, 229, 0.08)'
              }
            }}
          >
            <ListItemText
              primary={item}
              sx={{ 
                textAlign: 'left', 
                p: 2,
                '& .MuiTypography-root': {
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '1.1rem'
                }
              }}
            />
          </ListItem>
        ))}
        
        <Divider sx={{ my: 4, borderColor: 'rgba(226, 232, 240, 0.8)' }} />
        
        {/* Enhanced Mobile CTA Buttons */}
        <Box sx={{ px: 2, pb: 4 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<RocketIcon />}
            sx={{
              mb: 3,
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: 'white',
              fontWeight: 800,
              py: 3,
              borderRadius: 4,
              textTransform: 'none',
              fontSize: '1.1rem',
              boxShadow: '0 8px 25px rgba(79, 70, 229, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3730a3 0%, #6b21a8 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(79, 70, 229, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Start Free Trial
          </Button>
          
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PhoneIcon />}
            sx={{
              borderColor: '#cbd5e1',
              color: '#475569',
              fontWeight: 700,
              py: 3,
              borderRadius: 4,
              textTransform: 'none',
              fontSize: '1.1rem',
              borderWidth: 2,
              '&:hover': {
                borderColor: '#4f46e5',
                color: '#4f46e5',
                bgcolor: 'rgba(79, 70, 229, 0.05)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Schedule Demo
          </Button>
        </Box>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: scrolled 
            ? 'rgba(255, 255, 255, 0.95)' 
            : 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(20px)',
          color: scrolled ? '#0f172a' : 'white',
          boxShadow: scrolled 
            ? '0 4px 20px rgba(0,0,0,0.08)' 
            : '0 4px 20px rgba(0,0,0,0.1)',
          border: `1px solid ${scrolled 
            ? 'rgba(226, 232, 240, 0.8)' 
            : 'rgba(255, 255, 255, 0.1)'}`,
          zIndex: 1300,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ px: 0, py: 1.5, minHeight: '80px !important' }}>
            {/* Enhanced Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 3,
                    background: scrolled 
                      ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                      : 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: scrolled 
                      ? 'none' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.2rem',
                      color: scrolled ? 'white' : '#4f46e5',
                      letterSpacing: '-0.5px'
                    }}
                  >
                    R
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      fontSize: '1.8rem',
                      color: scrolled ? '#4f46e5' : 'white',
                      letterSpacing: '1px',
                      lineHeight: 1
                    }}
                  >
                    RONESIS
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: scrolled ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '2px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Enterprise Platform
                  </Typography>
                </Box>
              </Box>
            </Box>

            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Mobile Search */}
                <IconButton
                  onClick={() => setSearchOpen(!searchOpen)}
                  sx={{ 
                    color: scrolled ? '#64748b' : 'rgba(255, 255, 255, 0.8)',
                    bgcolor: scrolled 
                      ? 'rgba(100, 116, 139, 0.1)' 
                      : 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: scrolled 
                      ? '1px solid rgba(226, 232, 240, 0.8)' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: scrolled 
                        ? 'rgba(79, 70, 229, 0.1)' 
                        : 'rgba(255, 255, 255, 0.2)',
                      color: scrolled ? '#4f46e5' : 'white'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <SearchIcon />
                </IconButton>

                {/* Mobile Menu Button */}
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ 
                    color: scrolled ? '#4f46e5' : 'white',
                    bgcolor: scrolled 
                      ? 'rgba(79, 70, 229, 0.1)' 
                      : 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: scrolled 
                      ? '1px solid rgba(79, 70, 229, 0.2)' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    p: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: scrolled 
                        ? 'rgba(79, 70, 229, 0.15)' 
                        : 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Desktop Menu Items */}
                {menuItems.map((item, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    startIcon={item.icon}
                    endIcon={<ArrowDownIcon />}
                    onClick={(e) => handleMenuOpen(e, item.title)}
                    sx={{
                      fontWeight: 700,
                      color: scrolled ? '#334155' : 'rgba(255, 255, 255, 0.9)',
                      px: 3,
                      py: 2,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: '1rem',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: scrolled 
                          ? 'rgba(79, 70, 229, 0.08)' 
                          : 'rgba(255, 255, 255, 0.1)',
                        color: scrolled ? '#4f46e5' : 'white',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {item.title}
                  </Button>
                ))}

                {simpleMenuItems.map((item, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    sx={{
                      fontWeight: 700,
                      color: scrolled ? '#334155' : 'rgba(255, 255, 255, 0.9)',
                      px: 3,
                      py: 2,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: '1rem',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: scrolled 
                          ? 'rgba(79, 70, 229, 0.08)' 
                          : 'rgba(255, 255, 255, 0.1)',
                        color: scrolled ? '#4f46e5' : 'white',
                        transform: 'translateY(-2px)'
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 8,
                        left: '50%',
                        width: 0,
                        height: 2,
                        bgcolor: scrolled ? '#4f46e5' : 'white',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                        borderRadius: 1
                      },
                      '&:hover::after': {
                        width: '60%'
                      }
                    }}
                  >
                    {item}
                  </Button>
                ))}

                {/* Search */}
                <IconButton
                  onClick={() => setSearchOpen(!searchOpen)}
                  sx={{
                    ml: 2,
                    color: scrolled ? '#64748b' : 'rgba(255, 255, 255, 0.8)',
                    bgcolor: scrolled 
                      ? 'rgba(100, 116, 139, 0.1)' 
                      : 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: scrolled 
                      ? '1px solid rgba(226, 232, 240, 0.8)' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    '&:hover': {
                      color: scrolled ? '#4f46e5' : 'white',
                      bgcolor: scrolled 
                        ? 'rgba(79, 70, 229, 0.1)' 
                        : 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <SearchIcon />
                </IconButton>

                {/* Notifications */}
                <Badge badgeContent={3} color="error">
                  <IconButton
                    sx={{
                      color: scrolled ? '#64748b' : 'rgba(255, 255, 255, 0.8)',
                      bgcolor: scrolled 
                        ? 'rgba(100, 116, 139, 0.1)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: scrolled 
                        ? '1px solid rgba(226, 232, 240, 0.8)' 
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        color: scrolled ? '#4f46e5' : 'white',
                        bgcolor: scrolled 
                          ? 'rgba(79, 70, 229, 0.1)' 
                          : 'rgba(255, 255, 255, 0.2)',
                        transform: 'scale(1.05)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <NotificationIcon />
                  </IconButton>
                </Badge>

                {/* CTA Buttons */}
                <Button
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  sx={{
                    ml: 3,
                    borderColor: scrolled 
                      ? 'rgba(79, 70, 229, 0.3)' 
                      : 'rgba(255, 255, 255, 0.3)',
                    color: scrolled ? '#4f46e5' : 'white',
                    fontWeight: 700,
                    px: 4,
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderWidth: 2,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: scrolled ? '#4f46e5' : 'white',
                      bgcolor: scrolled 
                        ? 'rgba(79, 70, 229, 0.08)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Schedule Demo
                </Button>

                <Button
                  variant="contained"
                  startIcon={<RocketIcon />}
                  sx={{
                    background: scrolled 
                      ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                      : 'rgba(255, 255, 255, 0.9)',
                    color: scrolled ? 'white' : '#4f46e5',
                    fontWeight: 800,
                    px: 5,
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    border: scrolled 
                      ? 'none' 
                      : '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: scrolled ? 'none' : 'blur(10px)',
                    boxShadow: scrolled 
                      ? '0 4px 15px rgba(79, 70, 229, 0.3)' 
                      : '0 4px 15px rgba(0, 0, 0, 0.2)',
                    '&:hover': {
                      background: scrolled 
                        ? 'linear-gradient(135deg, #3730a3 0%, #6b21a8 100%)'
                        : 'white',
                      color: scrolled ? 'white' : '#4f46e5',
                      transform: 'translateY(-3px)',
                      boxShadow: scrolled 
                        ? '0 8px 25px rgba(79, 70, 229, 0.4)' 
                        : '0 8px 25px rgba(0, 0, 0, 0.3)'
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Start Free Trial
                </Button>

                {/* User Account */}
                <Avatar
                  sx={{
                    ml: 2,
                    width: 45,
                    height: 45,
                    bgcolor: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    cursor: 'pointer',
                    border: scrolled 
                      ? '2px solid rgba(79, 70, 229, 0.2)' 
                      : '2px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 8px 25px rgba(79, 70, 229, 0.3)'
                    }
                  }}
                >
                  <AccountIcon />
                </Avatar>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Enhanced Search Overlay */}
      <Fade in={searchOpen}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(20px)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 15
          }}
          onClick={() => setSearchOpen(false)}
        >
          <Container maxWidth="md">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <TextField
                fullWidth
                placeholder="Search documentation, features, and more..."
                variant="outlined"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#4f46e5' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.2rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(79, 70, 229, 0.3)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4f46e5'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4f46e5'
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    py: 3
                  }
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  display: 'block',
                  color: '#64748b',
                  textAlign: 'center'
                }}
              >
                Press ESC to close • Enter to search
              </Typography>
            </Paper>
          </Container>
        </Box>
      </Fade>

      {/* Enhanced Dropdown Menus */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            mt: 1,
            minWidth: 400,
            maxWidth: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(20px)',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            overflow: 'visible'
          }
        }}
      >
        {currentMenu && (
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#1e293b',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              {menuItems.find(item => item.title === currentMenu)?.icon}
              {currentMenu}
            </Typography>
            
            <Stack spacing={1}>
              {menuItems.find(item => item.title === currentMenu)?.items.map((subItem, index) => (
                <Box
                  key={index}
                  onClick={handleMenuClose}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid transparent',
                    '&:hover': {
                      bgcolor: 'rgba(79, 70, 229, 0.05)',
                      borderColor: 'rgba(79, 70, 229, 0.1)',
                      transform: 'translateX(8px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: '#1e293b'
                      }}
                    >
                      {subItem.name}
                    </Typography>
                    {subItem.new && (
                      <Chip 
                        label="New" 
                        size="small" 
                        sx={{ 
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: '#10b981',
                          color: 'white',
                          fontWeight: 700
                        }} 
                      />
                    )}
                    {subItem.popular && (
                      <Chip 
                        label="Popular" 
                        size="small" 
                        sx={{ 
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: '#f59e0b',
                          color: 'white',
                          fontWeight: 700
                        }} 
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      lineHeight: 1.5
                    }}
                  >
                    {subItem.desc}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Menu>

      {/* Enhanced Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 350,
            borderRadius: '0 25px 25px 0',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default HeaderPremium;
